import React, { FC, useContext, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/react-hooks';
import triggerTracking from '@blueprint/tracking';
import { useModal, Modal, Icon } from '@blueprint/ui';

import {
    XhrReponseModalContent,
    ResponseType
} from './XhrResponseModalContent';
import {
    AlertPanelWrapper,
    AlertPanelButtonLink,
    LeftAlignButtonWithIcon,
    PaddedDivider
} from './AlertPanel.styles';
import {
    filtersValuesSelector,
    isTravelTimeSearchSelector
} from '../../reducers/filters';
import { polyencSelector } from '../../reducers/searchResults';
import { SearchContext } from '../SearchContext';
import { getTranslator } from '../../shared/i18n/getTranslator';
import { trackEvent } from 'shared/helpers/segment/trackEvent';

import searchTranslations from 'public/locales/en/search.json';
import { SectionThatDisappearsWhenNoSideBar } from '../Layout/Layout.styles';
import { EditSavedSearchBanner } from '../banners/EditSavedSearchBanner';
import {
    constructEditSavedSearchFields,
    isShowEditSavedSearchBanner
} from '../helpers';
import { alertLinkClickHandler } from '../common/alertLinkClickHandler';
import AlertModal from 'shared/components/AlertModal';

import { CREATE_SAVED_SEARCH } from 'queries/search/editSavedSearch';
import { CREATE_EMAIL_ALERT } from 'queries/search/editCreateAlert';
import { selectTaxonomy } from 'reducers/taxonomy';
import { isOptimizelyFeatureEnabled } from './utils/isOptimizelyFeatureEnabled';
import { EventNames } from 'shared/tracking/contracts';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import { Links } from '../SearchContext';

const t = getTranslator(searchTranslations);
const editSavedSearchBtnText = t('actionBar.optionsList.editSavedSearch');
const startNewSearchBtnText = t('actionBar.optionsList.startANewSearch');
const turnOnEmailAlertBtnText = t('actionBar.optionsList.turnOnEmailAlert');
const editEmailAlertBtnText = t('actionBar.optionsList.editEmailAlert');
const createAlertBtnText = t('actionBar.optionsList.createAlert');
const saveSearchBtnText = t('actionBar.optionsList.saveSearch');
const travelTimeSearchBtnText = t('actionBar.optionsList.travelTimeSearch');
const drawYourSearchBtnText = t('actionBar.optionsList.drawYourSearch');

const fireTrackingEvent = (label: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'search results',
        eventAction: 'clickthrough',
        eventLabel: label
    });
};

type AlertPanelProps = {
    links: Links;
};

export const AlertPanel: FC<AlertPanelProps> = ({ links }) => {
    const [responseModalType, setResponseModalType] = useState<ResponseType>(
        null
    );

    const { state } = useContext(SearchContext);
    const router = useRouter();
    const isLoggedIn = useIsLoggedIn();

    const [isAlertModalShowing, openAlertModal, closeAlertModal] = useModal();
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);
    const taxonomyForSaveSearch = useSelector(selectTaxonomy);
    const polyenc = useSelector(polyencSelector);
    const { query, push, asPath } = router;

    const {
        userAlertId,
        savedSearchAndAlerts,
        searchType,
        previousSavedSearchUri,
        isEditSavedSearchBannerVisible
    } = state;

    const filters = useSelector(filtersValuesSelector);

    const onSaveSearchComplete = (status: number) => {
        if (status === 200) {
            setResponseModalType('saveSearchSuccess');
        } else {
            setResponseModalType('saveSearchFailure');
        }
    };

    const onSaveAlertComplete = (status: number) => {
        if (status === 200) {
            setResponseModalType('saveAlertSuccess');
        } else {
            setResponseModalType('saveAlertFailure');
        }
    };

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            onSaveSearchComplete(data.createNewSavedSearch.status);
        },
        onError: () => {
            setResponseModalType('saveSearchFailure');
        }
    });

    const [createEmailAlertMutation] = useMutation(CREATE_EMAIL_ALERT, {
        onCompleted: (data) => {
            onSaveAlertComplete(data.createNewEmailAlert.status);
        },
        onError: () => {
            setResponseModalType('saveAlertFailure');
        }
    });

    const onClick = (
        evt: MouseEvent<HTMLAnchorElement>,
        label?: string,
        requiresAuth = false
    ) => {
        evt.preventDefault();

        if (label) {
            fireTrackingEvent(label);
        }

        alertLinkClickHandler({
            evt,
            router,
            requiresAuth,
            loggedIn: !!isLoggedIn
        });
    };

    const createSavedSearchOrAlert = (label?: string) => {
        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            createNew: true,
            polyenc
        });

        if (label !== 'create_alert') {
            createSavedSearchMutation({
                variables: {
                    input
                }
            });
        } else {
            createEmailAlertMutation({
                variables: {
                    input
                }
            });
        }
    };

    const onAjaxLinkClick = async (
        evt: MouseEvent<HTMLAnchorElement>,
        label?: string
    ) => {
        evt.preventDefault();

        const destinationUrl = evt.currentTarget.dataset.url as string;
        const redirectUrl = encodeURIComponent(destinationUrl || asPath);
        const registerUrl = `${
            process.env.REGISTER_PATH || '/register/'
        }?page_after_login=${redirectUrl}&return_url=${redirectUrl}`;

        if (label) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'clickthrough',
                eventLabel: label
            });

            if (label === 'save_search') {
                trackEvent(
                    EventNames.CLICK_SAVE_SEARCH,
                    taxonomyForSaveSearch.taxonomy
                );
            }
        }

        if (!isLoggedIn) {
            if (label === 'save_search') {
                push(registerUrl);
                return;
            }

            if (label === 'create_alert') {
                const modalEnabled = await isOptimizelyFeatureEnabled({
                    feature: 'alert_modal',
                    query
                });

                if (!modalEnabled) {
                    push(registerUrl);
                    return;
                }

                openAlertModal(null);

                return;
            }
        }
        createSavedSearchOrAlert(label);
    };

    const onModalCloseClick = () => {
        setResponseModalType(null);
    };

    const isSavedSearch = userAlertId !== null;

    return (
        <>
            <AlertModal
                isShowing={isAlertModalShowing}
                close={closeAlertModal}
            />
            {isSavedSearch ? (
                isLoggedIn &&
                isEditSavedSearchBannerVisible &&
                isShowEditSavedSearchBanner(
                    userAlertId,
                    previousSavedSearchUri,
                    savedSearchAndAlerts?.uri
                ) ? (
                    <SectionThatDisappearsWhenNoSideBar minWidth="0">
                        <AlertPanelWrapper
                            color="graphic-background-accent"
                            data-testid="edit-saved-search-cta-banner-panel"
                        >
                            <EditSavedSearchBanner />
                        </AlertPanelWrapper>
                    </SectionThatDisappearsWhenNoSideBar>
                ) : (
                    <AlertPanelWrapper
                        color="graphic-background-accent-light"
                        data-testid="saved-search-email-alert-button-panel"
                    >
                        <AlertPanelButtonLink
                            variant="secondary"
                            data-testid="edit-saved-search-button-link"
                            data-url={savedSearchAndAlerts?.uri as string}
                            isFullWidth
                            onClick={(evt) => onClick(evt, 'edit_search', true)}
                        >
                            <Icon iconId="pencil" />
                            {editSavedSearchBtnText}
                        </AlertPanelButtonLink>

                        <AlertPanelButtonLink
                            variant="secondary"
                            data-testid="start-new-search-button-link"
                            data-url={`/${searchType}/`}
                            isFullWidth
                            onClick={(evt) => onClick(evt, 'new_search')}
                        >
                            <Icon iconId="search" />
                            {startNewSearchBtnText}
                        </AlertPanelButtonLink>

                        <AlertPanelButtonLink
                            variant="secondary"
                            data-testid="turn-on-edit-email-alert-button"
                            data-url={savedSearchAndAlerts?.uri as string}
                            isFullWidth
                            onClick={(evt) =>
                                onClick(
                                    evt,
                                    savedSearchAndAlerts?.currentFrequency
                                        .value === ''
                                        ? 'create_alert'
                                        : 'edit_alert',
                                    true
                                )
                            }
                        >
                            <Icon iconId="email_outline" />
                            {savedSearchAndAlerts?.currentFrequency.value === ''
                                ? turnOnEmailAlertBtnText
                                : editEmailAlertBtnText}
                        </AlertPanelButtonLink>
                    </AlertPanelWrapper>
                )
            ) : null}

            <AlertPanelWrapper>
                {!isSavedSearch ? (
                    <>
                        <AlertPanelButtonLink
                            variant="secondary"
                            data-testid="create-email-alert-button"
                            data-url={links?.createAlert || ''}
                            isFullWidth
                            onClick={(evt) =>
                                onAjaxLinkClick(evt, 'create_alert')
                            }
                        >
                            <Icon iconId="email_outline" />
                            {createAlertBtnText}
                        </AlertPanelButtonLink>
                        <AlertPanelButtonLink
                            variant="secondary"
                            data-testid="save-search-button"
                            data-url={links?.saveSearch || ''}
                            isFullWidth
                            onClick={(evt) =>
                                onAjaxLinkClick(evt, 'save_search')
                            }
                        >
                            <Icon iconId="save_outline" />
                            {saveSearchBtnText}
                        </AlertPanelButtonLink>
                    </>
                ) : null}
                <PaddedDivider variant="horizontal" />
                {!isTravelTimeSearch ? (
                    <LeftAlignButtonWithIcon
                        variant="tertiary"
                        data-testid="travel-time-search-button-link"
                        data-url="/travel-time/"
                        isFullWidth
                        onClick={(evt) => onClick(evt, 'travel_time_search')}
                    >
                        <Icon iconId="history" />
                        {travelTimeSearchBtnText}
                    </LeftAlignButtonWithIcon>
                ) : null}
                <LeftAlignButtonWithIcon
                    variant="tertiary"
                    data-testid="draw-your-search-button-link"
                    href={links?.mapView || ''}
                    rel="nofollow"
                    onClick={() => fireTrackingEvent('draw_search')}
                    isFullWidth
                >
                    <Icon iconId="draw" />
                    {drawYourSearchBtnText}
                </LeftAlignButtonWithIcon>
            </AlertPanelWrapper>

            <Modal
                isShowing={responseModalType !== null}
                close={onModalCloseClick}
                css={{ borderRadius: 0 }}
            >
                <XhrReponseModalContent
                    responseType={responseModalType}
                    close={onModalCloseClick}
                    action={
                        responseModalType === 'saveSearchFailure'
                            ? () => createSavedSearchOrAlert('save_search')
                            : responseModalType === 'saveAlertFailure'
                            ? () => createSavedSearchOrAlert('save_alert')
                            : undefined
                    }
                />
            </Modal>
        </>
    );
};
